import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { Shield, Plus, Search, Filter, Edit, Trash2, Users, ChevronDown, CheckCircle, Mail } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ModalPortal } from "@/components/ds/ModalPortal";

// Tipo específico para usuários do evento
interface UsuarioEvento {
  id: string;
  nome: string;
  telefone: string;
  email: string;
  tipoUsuario: 'Agência' | 'Promotor';
  solucoes: string[];
  status: 'convite_enviado' | 'confirmado';
}

export default function EventsGestaoAcesso() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const url = searchParams.get('url');
  const [usuarios, setUsuarios] = useState<UsuarioEvento[]>([]);
  const [filteredUsuarios, setFilteredUsuarios] = useState<UsuarioEvento[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("todos");
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<string>('');

  // Mock data para usuários do evento
  const mockUsuariosEvento: UsuarioEvento[] = [
    {
      id: "ue1",
      nome: "Carlos Silva",
      telefone: "(11) 99999-1234",
      email: "carlos@agencia.com",
      tipoUsuario: "Agência",
      solucoes: ["Quiz Interativo", "Caça ao Tesouro"],
      status: "confirmado"
    },
    {
      id: "ue2",
      nome: "Ana Costa",
      telefone: "(21) 88888-5678",
      email: "ana@agencia.com",
      tipoUsuario: "Promotor",
      solucoes: ["Quiz Interativo"],
      status: "convite_enviado"
    },
    {
      id: "ue3",
      nome: "João Santos",
      telefone: "(31) 77777-9012",
      email: "joao@agencia.com",
      tipoUsuario: "Agência",
      solucoes: ["Roda da Sorte", "Enquete ao Vivo"],
      status: "confirmado"
    }
  ];

  useEffect(() => {
    // Simular carregamento dos dados
    const loadData = async () => {
      await new Promise(resolve => setTimeout(resolve, 800));
      setUsuarios(mockUsuariosEvento);
      setFilteredUsuarios(mockUsuariosEvento);
      setIsLoading(false);
    };

    loadData();
  }, []);

  // Filtrar usuários baseado na busca e status
  useEffect(() => {
    let filtered = usuarios;

    if (searchTerm) {
      filtered = filtered.filter(usuario =>
        usuario.nome.toLowerCase().includes(searchTerm.toLowerCase()) ||
        usuario.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "todos") {
      filtered = filtered.filter(usuario => usuario.status === statusFilter);
    }

    setFilteredUsuarios(filtered);
  }, [usuarios, searchTerm, statusFilter]);

  // Fechar dropdown ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isStatusDropdownOpen && !target.closest('.status-dropdown-container')) {
        setIsStatusDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isStatusDropdownOpen]);

  const handleNovoUsuario = () => {
    navigate(`/eventos-admin/gestao-acesso/novo${location.search}`);
  };

  const handleEditarUsuario = (id: string) => {
    navigate(`/eventos-admin/gestao-acesso/editar/${id}${location.search}`);
  };

  const handleExcluirUsuario = async () => {
    if (!userToDelete) return;
    
    try {
      // Aqui seria a chamada para a API
      setUsuarios(prev => prev.filter(u => u.id !== userToDelete));
      toast({
        title: "Sucesso",
        description: "Usuário excluído com sucesso!",
      });
      setShowDeleteModal(false);
      setUserToDelete('');
    } catch (error) {
      toast({
        title: "Erro",
        description: "Não foi possível excluir o usuário",
        variant: "destructive",
      });
      setShowDeleteModal(false);
      setUserToDelete('');
    }
  };

  const getStatusIcon = (status: string) => {
    return status === 'confirmado' ? CheckCircle : Mail;
  };

  const getStatusColor = (status: string) => {
    return status === 'confirmado' 
      ? 'text-status-ok bg-brand-green/10' 
      : 'text-primary bg-primary/10';
  };

  const getTipoUsuarioColor = (tipo: string) => {
    return tipo === 'Agência' 
      ? 'text-brand-logo-2 bg-brand-logo-2/10' 
      : 'text-status-attention bg-brand-attention/10';
  };

  const stats = {
    total: usuarios.length,
    agencia: usuarios.filter(u => u.tipoUsuario === 'Agência').length,
    promotor: usuarios.filter(u => u.tipoUsuario === 'Promotor').length,
    confirmados: usuarios.filter(u => u.status === 'confirmado').length,
    convitesEnviados: usuarios.filter(u => u.status === 'convite_enviado').length
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 flex-none items-center justify-center rounded-[var(--radius)] bg-brand-logo-2/10">
          <Shield className="text-brand-logo-2" size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-[16px] font-bold text-foreground">Gestão de Acesso do Evento</h1>
          <p className="text-[13px] text-muted-foreground">
            Gerencie usuários e permissões específicas do evento
          </p>
          {url && (
            <p className="text-sm text-brand-logo-1 mt-1">
              Conectado a: {decodeURIComponent(url)}
            </p>
          )}
        </div>
        <button onClick={handleNovoUsuario} className="btn-primary flex items-center gap-2">
          <Plus size={20} />
          Novo Usuário
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-logo-1/10 rounded-full flex items-center justify-center">
              <Users className="text-brand-logo-1" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total</p>
              <p className="text-xl font-bold text-foreground">{stats.total}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-logo-2/10 rounded-full flex items-center justify-center">
              <Users className="text-brand-logo-2" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Agência</p>
              <p className="text-xl font-bold text-foreground">{stats.agencia}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-attention/10 rounded-full flex items-center justify-center">
              <Users className="text-status-attention" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Promotor</p>
              <p className="text-xl font-bold text-foreground">{stats.promotor}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-green/10 rounded-full flex items-center justify-center">
              <CheckCircle className="text-status-ok" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Confirmados</p>
              <p className="text-xl font-bold text-foreground">{stats.confirmados}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
              <Mail className="text-primary" size={20} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Convites Enviados</p>
              <p className="text-xl font-bold text-foreground">{stats.convitesEnviados}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl border border-border p-6">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" size={18} />
              <input
                type="text"
                placeholder="Buscar por nome ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-full border border-input bg-card py-2 pl-10 pr-4 text-[13px] text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="relative status-dropdown-container">
            <button
              onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}
              className="flex items-center gap-2 px-4 py-2 bg-muted border border-border rounded-lg text-foreground hover:bg-opacity-80 transition-colors min-w-[140px] justify-between"
            >
              <div className="flex items-center gap-2">
                <Filter size={16} />
                <span className="text-sm">
                  {statusFilter === "todos" ? "Todos" : statusFilter === "confirmado" ? "Confirmados" : "Convites Enviados"}
                </span>
              </div>
              <ChevronDown 
                className={`transition-transform duration-200 ${isStatusDropdownOpen ? 'rotate-180' : ''}`} 
                size={16} 
              />
            </button>
            
            {isStatusDropdownOpen && (
              <div className="absolute top-full right-0 mt-1 bg-muted border border-border rounded-xl shadow-lg z-10 min-w-[140px]">
                {[
                  { value: "todos", label: "Todos" },
                  { value: "confirmado", label: "Confirmados" },
                  { value: "convite_enviado", label: "Convites Enviados" }
                ].map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setStatusFilter(option.value);
                      setIsStatusDropdownOpen(false);
                    }}
                    className="w-full px-4 py-3 text-left text-foreground hover:bg-brand-logo-1/10 transition-colors first:rounded-t-xl last:rounded-b-xl"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-card rounded-xl border border-border overflow-hidden">
        {isLoading ? (
          <div className="p-6">
            <div className="space-y-4">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="animate-pulse flex items-center gap-4">
                  <div className="w-12 h-12 bg-muted rounded-full"></div>
                  <div className="flex-1 space-y-2">
                    <div className="h-4 bg-muted rounded w-1/4"></div>
                    <div className="h-3 bg-muted rounded w-1/3"></div>
                  </div>
                  <div className="w-20 h-8 bg-muted rounded"></div>
                  <div className="w-24 h-8 bg-muted rounded"></div>
                </div>
              ))}
            </div>
          </div>
        ) : filteredUsuarios.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-20 h-20 bg-brand-logo-2/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Shield className="text-brand-logo-2" size={40} />
            </div>
            <h3 className="text-[16px] font-semibold text-foreground mb-2">Nenhum usuário encontrado</h3>
            <p className="text-muted-foreground mb-6">
              {searchTerm || statusFilter !== "todos" 
                ? "Tente ajustar os filtros para encontrar usuários" 
                : "Começe adicionando seu primeiro usuário"
              }
            </p>
            {!searchTerm && statusFilter === "todos" && (
              <button onClick={handleNovoUsuario} className="btn-primary">
                Adicionar Primeiro Usuário
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-muted border-b border-border">
                <tr>
                  <th className="text-left min-w-0 px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Usuário</th>
                  <th className="text-left whitespace-nowrap px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Contato</th>
                  <th className="text-left whitespace-nowrap px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Tipo de Usuário</th>
                  <th className="text-left whitespace-nowrap px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Soluções</th>
                  <th className="text-left whitespace-nowrap px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Status</th>
                  <th className="text-right whitespace-nowrap px-4 pt-4 pb-3 text-[12px] font-semibold text-[var(--text-faint-2)]">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {filteredUsuarios.map((usuario) => {
                  const StatusIcon = getStatusIcon(usuario.status);
                  
                  return (
                    <tr key={usuario.id} className="hover:bg-[var(--tabela-linha-hover)] transition-colors">
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-brand-logo-2/10 rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-brand-logo-2 font-medium">
                              {usuario.nome.split(' ').map(n => n[0]).join('').substring(0, 2)}
                            </span>
                          </div>
                          <div className="min-w-0 flex-1">
                            <p className="font-medium text-foreground truncate">{usuario.nome}</p>
                            <p className="text-sm text-muted-foreground truncate">{usuario.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <p className="text-foreground truncate">{usuario.telefone}</p>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${getTipoUsuarioColor(usuario.tipoUsuario)}`}>
                          {usuario.tipoUsuario}
                        </span>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {usuario.solucoes.slice(0, 3).map((solucao, index) => (
                            <span key={index} className="px-2 py-1 bg-brand-logo-1/10 text-brand-logo-1 rounded-full text-xs truncate">
                              {solucao}
                            </span>
                          ))}
                          {usuario.solucoes.length > 3 && (
                            <span className="px-2 py-1 bg-brand-logo-1/10 text-brand-logo-1 rounded-full text-xs">
                              +{usuario.solucoes.length - 3}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(usuario.status)}`}>
                          <StatusIcon size={16} />
                          {usuario.status === 'confirmado' ? 'Confirmado' : 'Convite Enviado'}
                        </div>
                      </td>
                      <td className="px-4 py-4 align-middle text-[13px]">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleEditarUsuario(usuario.id)}
                            className="p-2 hover:bg-brand-logo-1/10 rounded-lg transition-colors group"
                            title="Editar usuário"
                          >
                            <Edit size={16} className="text-muted-foreground group-hover:text-brand-logo-1" />
                          </button>
                          <button
                            onClick={() => {
                              setUserToDelete(usuario.id);
                              setShowDeleteModal(true);
                            }}
                            className="p-2 hover:bg-brand-danger/10 rounded-lg transition-colors group"
                            title="Excluir usuário"
                          >
                            <Trash2 size={16} className="text-muted-foreground group-hover:text-status-danger" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDeleteModal && (
        <ModalPortal><div className="fixed inset-0 bg-scrim/80 flex items-center justify-center z-50 p-4">
          <div className="bg-card p-6 rounded-xl border border-border w-full max-w-md">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-brand-danger/10 rounded-ds-card">
                <Trash2 className="text-status-danger" size={24} />
              </div>
              <h3 className="text-[16px] font-semibold text-foreground">Confirmar Exclusão</h3>
            </div>
            <p className="text-muted-foreground mb-6">
              Tem certeza que deseja excluir este usuário? Esta ação não pode ser desfeita.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 text-muted-foreground border border-border rounded-lg hover:bg-muted transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleExcluirUsuario}
                className="px-4 py-2 bg-brand-danger text-destructive-foreground rounded-lg hover:bg-brand-danger transition-colors"
              >
                Excluir
              </button>
            </div>
          </div>
        </div></ModalPortal>
      )}
    </div>
  );
}